import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { monitoringAPI } from '../services/api';

type Tab = 'tasks' | 'workers' | 'cleaned' | 'tokens';

const ITEMS_PER_PAGE = 20;

// Retention: cleanup keeps ~93 days on disk, but UI hides anything older than
// start_of_current_month − 2 months so a couple of residual "old-month" days
// from the previous cleanup cycle never appear.
const VISIBLE_MONTHS_BACK = 2;

const MONTH_NAMES_RU = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

function computeVisibleCutoff(): Date {
  const now = new Date();
  const startOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
  const cutoff = new Date(startOfCurrentMonth);
  cutoff.setMonth(cutoff.getMonth() - VISIBLE_MONTHS_BACK);
  return cutoff;
}

interface MonthGroup<T> {
  key: string;   // "2026-07"
  label: string; // "Июль 2026"
  items: T[];
}

function groupByMonth<T extends { created_at: string | null }>(items: T[]): MonthGroup<T>[] {
  const groups: Record<string, T[]> = {};
  for (const item of items) {
    if (!item.created_at) continue;
    const d = new Date(item.created_at);
    if (isNaN(d.getTime())) continue;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    (groups[key] = groups[key] || []).push(item);
  }
  return Object.entries(groups)
    .sort(([a], [b]) => b.localeCompare(a))  // newest month first
    .map(([key, items]) => {
      const [year, monthStr] = key.split('-');
      const monthIdx = parseInt(monthStr, 10) - 1;
      return {
        key,
        label: `${MONTH_NAMES_RU[monthIdx] ?? monthStr} ${year}`,
        items,
      };
    });
}

interface TaskInfo {
  id: string;
  short_id: string | null;
  job_type: string;
  status: string;
  username: string;
  user_id: string;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  progress_percent: number;
  error_message: string | null;
  queue: string | null;
}

interface TasksData {
  tasks: TaskInfo[];
  total: number;
  pending: number;
  processing: number;
  completed: number;
  error: number;
}

interface WorkerInfo {
  name: string;
  status: string;
  active_tasks: number;
  processed: number;
  queues: string[];
}

interface QueueInfo {
  name: string;
  length: number;
  consumers: number;
}

interface WorkersData {
  workers: WorkerInfo[];
  total_workers: number;
  active_workers: number;
  queues: QueueInfo[];
  total_pending_tasks: number;
}

interface Stats {
  jobs: {
    total: number;
    pending: number;
    processing: number;
    completed: number;
    failed: number;
    by_type: {
      analyze_price: number;
      analyze_property: number;
    };
  };
  users: {
    total: number;
    active: number;
    limit: number;
  };
  recent: {
    jobs_24h: number;
  };
}

interface CleanedJobInfo {
  job_id: string;
  short_id: string | null;
  user_id: string;
  username: string;
  job_type: string;
  job_name: string;
  status: string;
  created_at: string | null;
  completed_at: string | null;
}

interface CleanupHistoryData {
  cleanup_id: string | null;
  completed_at: string | null;
  jobs_cleaned_count: number;
  mb_freed: number;
  cleaned_jobs: CleanedJobInfo[];
  has_history: boolean;
}

interface TokenUsageJobInfo {
  job_id: string;
  short_id: string | null;
  job_type: string;
  job_name: string;
  username: string;
  workflow: string;
  total_llm_calls: number;
  total_vlm_calls: number;
  total_input_tokens: number;
  total_output_tokens: number;
  created_at: string | null;
}

interface TokenUsageUserInfo {
  username: string;
  total_input_tokens: number;
  total_output_tokens: number;
  total_llm_calls: number;
  total_vlm_calls: number;
  job_count: number;
}

interface TokenUsageData {
  grand_total_input: number;
  grand_total_output: number;
  grand_total_llm_calls: number;
  grand_total_vlm_calls: number;
  total_jobs_with_data: number;
  by_user: TokenUsageUserInfo[];
  by_job: TokenUsageJobInfo[];
}

interface TokenUsageStepInfo {
  step_name: string;
  step_label: string;
  llm_calls: number;
  vlm_calls: number;
  input_tokens: number;
  output_tokens: number;
  max_tokens_requested: number;
  duration_sec: number | null;
}

interface TokenUsageDetail {
  job_id: string;
  short_id: string | null;
  job_type: string;
  job_name: string;
  username: string;
  workflow: string;
  total_llm_calls: number;
  total_vlm_calls: number;
  total_input_tokens: number;
  total_output_tokens: number;
  steps: TokenUsageStepInfo[];
}

const MonitoringPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('tasks');
  const [tasksData, setTasksData] = useState<TasksData | null>(null);
  const [workersData, setWorkersData] = useState<WorkersData | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [cleanupHistory, setCleanupHistory] = useState<CleanupHistoryData | null>(null);
  const [tokenUsageData, setTokenUsageData] = useState<TokenUsageData | null>(null);
  const [tokenDetail, setTokenDetail] = useState<TokenUsageDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTasks, setTotalTasks] = useState(0);
  const totalPages = Math.ceil(totalTasks / ITEMS_PER_PAGE);

  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Which month-groups are collapsed in the tasks tab (persisted in localStorage)
  const [collapsedMonths, setCollapsedMonths] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('monitoring:collapsedMonths');
      return saved ? new Set(JSON.parse(saved) as string[]) : new Set();
    } catch {
      return new Set();
    }
  });

  const toggleMonth = (key: string) => {
    setCollapsedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        localStorage.setItem('monitoring:collapsedMonths', JSON.stringify(Array.from(next)));
      } catch { /* ignore */ }
      return next;
    });
  };

  const visibleCutoff = useMemo(() => computeVisibleCutoff(), []);
  const visibleGroupedTasks = useMemo(() => {
    const list = tasksData?.tasks ?? [];
    const filtered = list.filter((t) => {
      const d = new Date(t.created_at);
      return !isNaN(d.getTime()) && d >= visibleCutoff;
    });
    return groupByMonth(filtered);
  }, [tasksData, visibleCutoff]);

  const hiddenTasksCount = (tasksData?.tasks?.length ?? 0)
    - visibleGroupedTasks.reduce((n, g) => n + g.items.length, 0);

  const visibleGroupedTokenJobs = useMemo(() => {
    const list = tokenUsageData?.by_job ?? [];
    const filtered = list.filter((j) => {
      if (!j.created_at) return false;
      const d = new Date(j.created_at);
      return !isNaN(d.getTime()) && d >= visibleCutoff;
    });
    return groupByMonth(filtered);
  }, [tokenUsageData, visibleCutoff]);

  const hiddenTokenJobsCount = (tokenUsageData?.by_job?.length ?? 0)
    - visibleGroupedTokenJobs.reduce((n, g) => n + g.items.length, 0);

  const loadData = useCallback(async () => {
    try {
      setError(null);

      if (activeTab === 'tasks') {
        const offset = (currentPage - 1) * ITEMS_PER_PAGE;
        const data = await monitoringAPI.getTasks(
          statusFilter || undefined,
          typeFilter || undefined,
          ITEMS_PER_PAGE,
          offset
        );
        setTasksData(data);
        setTotalTasks(data.total || 0);
      } else if (activeTab === 'workers') {
        const [workers, statsData] = await Promise.all([
          monitoringAPI.getWorkers(),
          monitoringAPI.getStats()
        ]);
        setWorkersData(workers);
        setStats(statsData);
      } else if (activeTab === 'cleaned') {
        const data = await monitoringAPI.getCleanupHistory();
        setCleanupHistory(data);
      } else if (activeTab === 'tokens') {
        const data = await monitoringAPI.getTokenUsage();
        setTokenUsageData(data);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Ошибка загрузки данных мониторинга');
    } finally {
      setLoading(false);
    }
  }, [activeTab, statusFilter, typeFilter, currentPage]);

  useEffect(() => {
    setLoading(true);
    loadData();
  }, [loadData]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, typeFilter]);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Auto-refresh every 5 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadData();
    }, 5000);

    return () => clearInterval(interval);
  }, [autoRefresh, loadData]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr.includes('Z') ? dateStr : dateStr + 'Z');
    return date.toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return { bg: '#fff3e0', color: '#e65100' };
      case 'processing': return { bg: '#e3f2fd', color: '#1565c0' };
      case 'completed': return { bg: '#e8f5e9', color: '#2e7d32' };
      case 'error': return { bg: '#ffebee', color: '#c62828' };
      default: return { bg: '#f5f5f5', color: '#666' };
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Ожидает';
      case 'processing': return 'Выполняется';
      case 'completed': return 'Завершено';
      case 'error': return 'Ошибка';
      default: return status;
    }
  };

  const getJobTypeText = (type: string) => {
    switch (type) {
      case 'analyze_price': return 'Анализ цен';
      case 'analyze_property': return 'Анализ характеристик';
      case 'cleanup': return 'Очистка';
      default: return type;
    }
  };

  const tabStyle = (isActive: boolean) => ({
    padding: '12px 24px',
    backgroundColor: isActive ? '#1976d2' : '#f5f5f5',
    color: isActive ? 'white' : '#333',
    border: 'none',
    borderRadius: '4px 4px 0 0',
    cursor: 'pointer',
    fontWeight: isActive ? 'bold' : 'normal' as const,
    fontSize: '14px'
  });

  if (loading && !tasksData && !workersData && !cleanupHistory) {
    return <div style={{ padding: '20px' }}>Загрузка...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>Мониторинг</h2>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          Автообновление (5 сек)
        </label>
      </div>

      {error && (
        <div style={{
          color: '#d32f2f',
          backgroundColor: '#ffebee',
          padding: '10px',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '0' }}>
        <button style={tabStyle(activeTab === 'tasks')} onClick={() => setActiveTab('tasks')}>
          Задачи
        </button>
        <button style={tabStyle(activeTab === 'workers')} onClick={() => setActiveTab('workers')}>
          Воркеры и очереди
        </button>
        <button style={tabStyle(activeTab === 'cleaned')} onClick={() => setActiveTab('cleaned')}>
          Очищенные
        </button>
        <button style={tabStyle(activeTab === 'tokens')} onClick={() => { setActiveTab('tokens'); setTokenDetail(null); }}>
          Использование токенов
        </button>
      </div>

      {/* Content */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0 8px 8px 8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '20px'
      }}>
        {activeTab === 'tasks' && tasksData && (
          <div>
            {/* Stats Summary */}
            <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <div style={{ padding: '15px 20px', backgroundColor: '#f5f5f5', borderRadius: '8px', minWidth: '120px' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{tasksData.total}</div>
                <div style={{ color: '#666', fontSize: '14px' }}>Всего</div>
              </div>
              <div style={{ padding: '15px 20px', backgroundColor: '#fff3e0', borderRadius: '8px', minWidth: '120px' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#e65100' }}>{tasksData.pending}</div>
                <div style={{ color: '#666', fontSize: '14px' }}>Ожидает</div>
              </div>
              <div style={{ padding: '15px 20px', backgroundColor: '#e3f2fd', borderRadius: '8px', minWidth: '120px' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1565c0' }}>{tasksData.processing}</div>
                <div style={{ color: '#666', fontSize: '14px' }}>Выполняется</div>
              </div>
              <div style={{ padding: '15px 20px', backgroundColor: '#e8f5e9', borderRadius: '8px', minWidth: '120px' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2e7d32' }}>{tasksData.completed}</div>
                <div style={{ color: '#666', fontSize: '14px' }}>Завершено</div>
              </div>
              <div style={{ padding: '15px 20px', backgroundColor: '#ffebee', borderRadius: '8px', minWidth: '120px' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#c62828' }}>{tasksData.error}</div>
                <div style={{ color: '#666', fontSize: '14px' }}>Ошибки</div>
              </div>
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                <option value="">Все статусы</option>
                <option value="pending">Ожидает</option>
                <option value="processing">Выполняется</option>
                <option value="completed">Завершено</option>
                <option value="error">Ошибка</option>
              </select>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                <option value="">Все типы</option>
                <option value="analyze_price">Анализ цен</option>
                <option value="analyze_property">Анализ характеристик</option>
                <option value="cleanup">Очистка</option>
              </select>
              <button
                onClick={loadData}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Обновить
              </button>
            </div>

            {/* Tasks Table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>ID</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Тип</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Статус</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Пользователь</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Создано</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Прогресс</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Очередь</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleGroupedTasks.map((group) => {
                    const isCollapsed = collapsedMonths.has(group.key);
                    return (
                      <React.Fragment key={group.key}>
                        <tr
                          onClick={() => toggleMonth(group.key)}
                          style={{
                            backgroundColor: '#eef2f7',
                            cursor: 'pointer',
                            userSelect: 'none',
                          }}
                        >
                          <td
                            colSpan={7}
                            style={{
                              padding: '10px 14px',
                              fontWeight: 600,
                              color: '#333',
                              borderBottom: '1px solid #d5dae1',
                            }}
                          >
                            <span style={{ display: 'inline-block', width: 16 }}>
                              {isCollapsed ? '▶' : '▼'}
                            </span>
                            {group.label}
                            <span style={{ color: '#777', fontWeight: 400, marginLeft: 8 }}>
                              ({group.items.length}{group.items.length === 1 ? ' задача' : ' задач'})
                            </span>
                          </td>
                        </tr>
                        {!isCollapsed && group.items.map((task) => {
                          const statusStyle = getStatusColor(task.status);
                          return (
                            <tr key={task.id} style={{ borderBottom: '1px solid #eee' }}>
                              <td style={{ padding: '12px' }}>
                                <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                  {task.short_id ? `#${task.short_id}` : task.id.slice(0, 20)}
                                </div>
                              </td>
                              <td style={{ padding: '12px' }}>{getJobTypeText(task.job_type)}</td>
                              <td style={{ padding: '12px', textAlign: 'center' }}>
                                <span style={{
                                  padding: '4px 12px',
                                  borderRadius: '20px',
                                  backgroundColor: statusStyle.bg,
                                  color: statusStyle.color,
                                  fontSize: '12px'
                                }}>
                                  {getStatusText(task.status)}
                                </span>
                              </td>
                              <td style={{ padding: '12px' }}>{task.username}</td>
                              <td style={{ padding: '12px', color: '#666' }}>{formatDate(task.created_at)}</td>
                              <td style={{ padding: '12px', textAlign: 'center' }}>
                                {task.status === 'processing' ? (
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                    <div style={{
                                      width: '60px',
                                      height: '8px',
                                      backgroundColor: '#e0e0e0',
                                      borderRadius: '4px',
                                      overflow: 'hidden'
                                    }}>
                                      <div style={{
                                        width: `${task.progress_percent}%`,
                                        height: '100%',
                                        backgroundColor: '#1976d2',
                                        transition: 'width 0.3s'
                                      }} />
                                    </div>
                                    <span style={{ fontSize: '12px' }}>{task.progress_percent}%</span>
                                  </div>
                                ) : '-'}
                              </td>
                              <td style={{ padding: '12px', color: '#666' }}>{task.queue || '-'}</td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                  {visibleGroupedTasks.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                        {(tasksData?.tasks?.length ?? 0) > 0
                          ? 'Нет задач за последние 3 месяца'
                          : 'Нет задач'}
                      </td>
                    </tr>
                  )}
                  {hiddenTasksCount > 0 && (
                    <tr>
                      <td colSpan={7} style={{
                        padding: '10px 14px',
                        color: '#888',
                        fontSize: '12px',
                        fontStyle: 'italic',
                        textAlign: 'center',
                        backgroundColor: '#fafafa',
                      }}>
                        Скрыто {hiddenTasksCount} {hiddenTasksCount === 1 ? 'задача' : 'задач'} старше {VISIBLE_MONTHS_BACK} полных месяцев — они будут удалены при следующей очистке.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                marginTop: '20px',
                paddingTop: '20px',
                borderTop: '1px solid #eee'
              }}>
                <button
                  onClick={() => goToPage(1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: currentPage === 1 ? '#e0e0e0' : '#1976d2',
                    color: currentPage === 1 ? '#999' : 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  {'<<'}
                </button>
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: currentPage === 1 ? '#e0e0e0' : '#1976d2',
                    color: currentPage === 1 ? '#999' : 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  {'<'}
                </button>

                <span style={{ padding: '8px 16px', fontSize: '14px' }}>
                  Страница {currentPage} из {totalPages} (всего: {totalTasks})
                </span>

                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: currentPage === totalPages ? '#e0e0e0' : '#1976d2',
                    color: currentPage === totalPages ? '#999' : 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  {'>'}
                </button>
                <button
                  onClick={() => goToPage(totalPages)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: currentPage === totalPages ? '#e0e0e0' : '#1976d2',
                    color: currentPage === totalPages ? '#999' : 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  {'>>'}
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'workers' && (
          <div>
            {/* Overall Stats */}
            {stats && (
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Общая статистика</h3>
                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                  <div style={{ padding: '15px 20px', backgroundColor: '#e3f2fd', borderRadius: '8px', minWidth: '150px' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1565c0' }}>{stats.jobs.total}</div>
                    <div style={{ color: '#666', fontSize: '14px' }}>Всего задач</div>
                  </div>
                  <div style={{ padding: '15px 20px', backgroundColor: '#f3e5f5', borderRadius: '8px', minWidth: '150px' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#7b1fa2' }}>{stats.recent.jobs_24h}</div>
                    <div style={{ color: '#666', fontSize: '14px' }}>Задач за 24ч</div>
                  </div>
                  <div style={{ padding: '15px 20px', backgroundColor: '#e8f5e9', borderRadius: '8px', minWidth: '150px' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2e7d32' }}>
                      {stats.users.active} / {stats.users.limit}
                    </div>
                    <div style={{ color: '#666', fontSize: '14px' }}>Пользователей</div>
                  </div>
                </div>

                <div style={{ marginTop: '15px', display: 'flex', gap: '20px' }}>
                  <div>
                    <strong>По типу:</strong>{' '}
                    Анализ цен: {stats.jobs.by_type.analyze_price},{' '}
                    Анализ характеристик: {stats.jobs.by_type.analyze_property}
                  </div>
                </div>
              </div>
            )}

            {/* Workers */}
            {workersData && (
              <>
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '15px' }}>
                    Воркеры ({workersData.active_workers} / {workersData.total_workers} активно)
                  </h3>

                  {workersData.workers.length > 0 ? (
                    <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                      {workersData.workers.map((worker) => (
                        <div
                          key={worker.name}
                          style={{
                            padding: '15px 20px',
                            backgroundColor: worker.status === 'online' ? '#e8f5e9' : '#ffebee',
                            borderRadius: '8px',
                            minWidth: '250px',
                            border: `1px solid ${worker.status === 'online' ? '#a5d6a7' : '#ef9a9a'}`
                          }}
                        >
                          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                            {worker.name.split('@')[1] || worker.name}
                          </div>
                          <div style={{ fontSize: '14px', color: '#666' }}>
                            <div>Статус: <span style={{ color: worker.status === 'online' ? '#2e7d32' : '#c62828' }}>
                              {worker.status === 'online' ? 'Онлайн' : 'Оффлайн'}
                            </span></div>
                            <div>Активных задач: {worker.active_tasks}</div>
                            <div>Обработано: {worker.processed}</div>
                            <div>Очереди: {worker.queues.join(', ') || '-'}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ padding: '20px', backgroundColor: '#fff3e0', borderRadius: '8px', color: '#e65100' }}>
                      Нет активных воркеров. Убедитесь, что Celery worker запущен.
                    </div>
                  )}
                </div>

                {/* Queues */}
                <div>
                  <h3 style={{ marginTop: 0, marginBottom: '15px' }}>
                    Очереди (ожидает: {workersData.total_pending_tasks})
                  </h3>

                  {workersData.queues.length > 0 ? (
                    <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                      {workersData.queues.map((queue) => (
                        <div
                          key={queue.name}
                          style={{
                            padding: '15px 20px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '8px',
                            minWidth: '200px'
                          }}
                        >
                          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>{queue.name}</div>
                          <div style={{ fontSize: '14px', color: '#666' }}>
                            <div>В очереди: <strong style={{ color: queue.length > 0 ? '#e65100' : '#2e7d32' }}>
                              {queue.length}
                            </strong></div>
                            <div>Консьюмеров: {queue.consumers}</div>
                          </div>
                          {/* Queue bar */}
                          <div style={{
                            marginTop: '10px',
                            height: '8px',
                            backgroundColor: '#e0e0e0',
                            borderRadius: '4px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${Math.min(queue.length * 10, 100)}%`,
                              height: '100%',
                              backgroundColor: queue.length > 5 ? '#ff9800' : '#4caf50',
                              transition: 'width 0.3s'
                            }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px', color: '#666' }}>
                      Нет данных об очередях
                    </div>
                  )}
                </div>
              </>
            )}

            <div style={{ marginTop: '20px' }}>
              <button
                onClick={loadData}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Обновить
              </button>
            </div>
          </div>
        )}

        {activeTab === 'cleaned' && (
          <div>
            {cleanupHistory && cleanupHistory.has_history ? (
              <>
                {/* Cleanup Summary Card */}
                <div style={{
                  marginBottom: '20px',
                  padding: '20px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}>
                  <h3 style={{ margin: '0 0 15px 0' }}>Последняя очистка</h3>
                  <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ color: '#666', fontSize: '12px' }}>ID очистки</div>
                      <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>{cleanupHistory.cleanup_id}</div>
                    </div>
                    <div>
                      <div style={{ color: '#666', fontSize: '12px' }}>Время</div>
                      <div>{formatDate(cleanupHistory.completed_at)}</div>
                    </div>
                    <div>
                      <div style={{ color: '#666', fontSize: '12px' }}>Задач очищено</div>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1976d2' }}>
                        {cleanupHistory.jobs_cleaned_count}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#666', fontSize: '12px' }}>Освобождено</div>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2e7d32' }}>
                        {cleanupHistory.mb_freed} МБ
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cleaned Jobs Table */}
                <h3 style={{ marginTop: 0, marginBottom: '15px' }}>
                  Очищенные задачи ({cleanupHistory.cleaned_jobs.length})
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5' }}>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>ID</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Тип</th>
                        <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Статус</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Пользователь</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Создано</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Завершено</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cleanupHistory.cleaned_jobs.map((job) => {
                        const statusStyle = getStatusColor(job.status);
                        return (
                          <tr key={job.job_id} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: '12px' }}>
                              <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                {job.short_id ? `#${job.short_id}` : job.job_id.slice(0, 20)}
                              </div>
                            </td>
                            <td style={{ padding: '12px' }}>{getJobTypeText(job.job_type)}</td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <span style={{
                                padding: '4px 12px',
                                borderRadius: '20px',
                                backgroundColor: statusStyle.bg,
                                color: statusStyle.color,
                                fontSize: '12px'
                              }}>
                                {getStatusText(job.status)}
                              </span>
                            </td>
                            <td style={{ padding: '12px' }}>{job.username}</td>
                            <td style={{ padding: '12px', color: '#666' }}>{formatDate(job.created_at)}</td>
                            <td style={{ padding: '12px', color: '#666' }}>{formatDate(job.completed_at)}</td>
                          </tr>
                        );
                      })}
                      {cleanupHistory.cleaned_jobs.length === 0 && (
                        <tr>
                          <td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                            Нет очищенных задач
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>-</div>
                <div>История очистки пуста</div>
                <div style={{ fontSize: '12px', marginTop: '8px' }}>
                  Информация появится после первой автоматической очистки
                </div>
              </div>
            )}

            <div style={{ marginTop: '20px' }}>
              <button
                onClick={loadData}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Обновить
              </button>
            </div>
          </div>
        )}

        {/* Token Usage Tab */}
        {activeTab === 'tokens' && tokenUsageData && !tokenDetail && (
          <div>
            {/* Summary cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '24px' }}>
              {[
                { label: 'Входные токены', value: tokenUsageData.grand_total_input },
                { label: 'Выходные токены', value: tokenUsageData.grand_total_output },
                { label: 'LLM вызовы', value: tokenUsageData.grand_total_llm_calls },
                { label: 'VLM вызовы', value: tokenUsageData.grand_total_vlm_calls },
                { label: 'Задач с данными', value: tokenUsageData.total_jobs_with_data },
              ].map((card, i) => (
                <div key={i} style={{ backgroundColor: '#f5f5f5', borderRadius: '8px', padding: '16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1976d2' }}>
                    {card.value.toLocaleString('ru-RU')}
                  </div>
                  <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>{card.label}</div>
                </div>
              ))}
            </div>

            {/* Per-user table */}
            {tokenUsageData.by_user.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>По пользователям</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Пользователь</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Входные токены</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Выходные токены</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>LLM</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>VLM</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Задач</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tokenUsageData.by_user.map((user, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '8px 12px' }}>{user.username}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>{user.total_input_tokens.toLocaleString('ru-RU')}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>{user.total_output_tokens.toLocaleString('ru-RU')}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>{user.total_llm_calls}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>{user.total_vlm_calls}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>{user.job_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Per-job table */}
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>По задачам</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>ID</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Тип</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Пользователь</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Вх. токены</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Вых. токены</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>LLM</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>VLM</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Дата</th>
                </tr>
              </thead>
              <tbody>
                {visibleGroupedTokenJobs.map((group) => {
                  const isCollapsed = collapsedMonths.has(`tokens:${group.key}`);
                  const sumIn  = group.items.reduce((n, j) => n + (j.total_input_tokens || 0), 0);
                  const sumOut = group.items.reduce((n, j) => n + (j.total_output_tokens || 0), 0);
                  const sumLlm = group.items.reduce((n, j) => n + (j.total_llm_calls || 0), 0);
                  const sumVlm = group.items.reduce((n, j) => n + (j.total_vlm_calls || 0), 0);
                  return (
                    <React.Fragment key={group.key}>
                      <tr
                        onClick={() => toggleMonth(`tokens:${group.key}`)}
                        style={{
                          backgroundColor: '#eef2f7',
                          cursor: 'pointer',
                          userSelect: 'none',
                          borderBottom: '1px solid #d5dae1',
                        }}
                      >
                        <td colSpan={3} style={{ padding: '10px 14px', fontWeight: 600, color: '#333' }}>
                          <span style={{ display: 'inline-block', width: 16 }}>
                            {isCollapsed ? '▶' : '▼'}
                          </span>
                          {group.label}
                          <span style={{ color: '#777', fontWeight: 400, marginLeft: 8 }}>
                            ({group.items.length}{group.items.length === 1 ? ' задача' : ' задач'})
                          </span>
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>
                          {sumIn.toLocaleString('ru-RU')}
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>
                          {sumOut.toLocaleString('ru-RU')}
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>
                          {sumLlm}
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#333' }}>
                          {sumVlm}
                        </td>
                        <td style={{ padding: '10px 12px', color: '#777', fontSize: '12px' }}>
                          итого за месяц
                        </td>
                      </tr>
                      {!isCollapsed && group.items.map((job, i) => (
                        <tr
                          key={`${group.key}-${i}`}
                          style={{ borderBottom: '1px solid #eee', cursor: 'pointer' }}
                          onClick={async () => {
                            try {
                              const detail = await monitoringAPI.getTokenUsageDetail(job.job_id);
                              setTokenDetail(detail);
                            } catch (err) {
                              console.error('Failed to load token detail', err);
                            }
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f7ff')}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '')}
                        >
                          <td style={{ padding: '8px 12px' }}>
                            <span style={{ backgroundColor: '#e3f2fd', color: '#1565c0', padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold' }}>
                              #{job.short_id || job.job_id.slice(-6)}
                            </span>
                          </td>
                          <td style={{ padding: '8px 12px' }}>
                            {job.job_type === 'analyze_price' ? 'Анализ цен' : job.job_type === 'analyze_property' ? 'Анализ характеристик' : job.job_type}
                          </td>
                          <td style={{ padding: '8px 12px' }}>{job.username}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right' }}>{job.total_input_tokens.toLocaleString('ru-RU')}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right' }}>{job.total_output_tokens.toLocaleString('ru-RU')}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right' }}>{job.total_llm_calls}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right' }}>{job.total_vlm_calls}</td>
                          <td style={{ padding: '8px 12px', fontSize: '13px', color: '#666' }}>{formatDate(job.created_at)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
                {visibleGroupedTokenJobs.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                      {(tokenUsageData.by_job?.length ?? 0) > 0
                        ? 'Нет задач с данными за последние 3 месяца'
                        : 'Нет задач с данными'}
                    </td>
                  </tr>
                )}
                {hiddenTokenJobsCount > 0 && (
                  <tr>
                    <td colSpan={8} style={{
                      padding: '10px 14px',
                      color: '#888',
                      fontSize: '12px',
                      fontStyle: 'italic',
                      textAlign: 'center',
                      backgroundColor: '#fafafa',
                    }}>
                      Скрыто {hiddenTokenJobsCount} {hiddenTokenJobsCount === 1 ? 'задача' : 'задач'} старше {VISIBLE_MONTHS_BACK} полных месяцев.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Token Usage Detail */}
        {activeTab === 'tokens' && tokenDetail && (
          <div>
            <button
              onClick={() => setTokenDetail(null)}
              style={{ marginBottom: '16px', padding: '8px 16px', backgroundColor: '#f5f5f5', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}
            >
              ← Назад к списку
            </button>

            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ margin: '0 0 8px 0' }}>
                <span style={{ backgroundColor: '#e3f2fd', color: '#1565c0', padding: '2px 8px', borderRadius: '4px', fontSize: '14px', marginRight: '8px' }}>
                  #{tokenDetail.short_id || tokenDetail.job_id.slice(-6)}
                </span>
                {tokenDetail.job_name}
              </h3>
              <div style={{ color: '#666', fontSize: '14px' }}>
                {tokenDetail.username} · {tokenDetail.job_type === 'analyze_price' ? 'Анализ цен' : 'Анализ характеристик'} · Всего: {(tokenDetail.total_input_tokens + tokenDetail.total_output_tokens).toLocaleString('ru-RU')} токенов
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Шаг</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>LLM</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>VLM</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Входные токены</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Выходные токены</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Длительность</th>
                </tr>
              </thead>
              <tbody>
                {tokenDetail.steps.map((step, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '8px 12px', fontWeight: 500 }}>{step.step_label}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{step.llm_calls}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{step.vlm_calls}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{step.input_tokens.toLocaleString('ru-RU')}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{step.output_tokens.toLocaleString('ru-RU')}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: '#666' }}>
                      {step.duration_sec != null
                        ? step.duration_sec >= 60
                          ? `${Math.floor(step.duration_sec / 60)} мин ${Math.round(step.duration_sec % 60)} с`
                          : `${Math.round(step.duration_sec)} с`
                        : '—'}
                    </td>
                  </tr>
                ))}
                {/* Total row */}
                <tr style={{ borderTop: '2px solid #ddd', fontWeight: 'bold' }}>
                  <td style={{ padding: '8px 12px' }}>Итого</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>{tokenDetail.total_llm_calls}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>{tokenDetail.total_vlm_calls}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>{tokenDetail.total_input_tokens.toLocaleString('ru-RU')}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>{tokenDetail.total_output_tokens.toLocaleString('ru-RU')}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', color: '#666' }}>
                    {tokenDetail.steps.reduce((sum, s) => sum + (s.duration_sec || 0), 0) >= 60
                      ? `${Math.floor(tokenDetail.steps.reduce((sum, s) => sum + (s.duration_sec || 0), 0) / 60)} мин`
                      : `${Math.round(tokenDetail.steps.reduce((sum, s) => sum + (s.duration_sec || 0), 0))} с`}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonitoringPage;
